import React from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { OptionType } from "commons/ui/input/Select";

import { notEmptyValidator } from "commons/utils/validate";

import { AttachmentType } from "components/ui/AttachmentPicker";

import { useSaveActionsContext } from "context/save";

import useCategoryApi from "hooks/api/private/useCategoryApi";
import useCollectionApi from "hooks/api/private/useCollectionApi";
import useProductApi from "hooks/api/private/useProductApi";
import useProductMeasureApi from "hooks/api/private/useProductMeasureApi";
import useProductModuleApi from "hooks/api/private/useProductModuleApi";
import useTemplateApi from "hooks/api/private/useTemplateApi";
import useLanguageOrderableImages, {
    LanguageOrderableImagesHook,
} from "hooks/useLanguageOrderableImages";
import useMobileOrDesktopFile from "hooks/useMobileOrDesktopFile";
import useSingleFile from "hooks/useSingleFile";

import { CategoryListItem } from "types/api/category";
import { DesktopMobileForm, MetaFields } from "types/api/commons";
import { ProductBody, ProductDetails } from "types/api/product";
import { DesktopOrMobile, IndoorOrOutdoor } from "types/enums";

import { emptyMetaFields, metaFieldsSchema } from "utils/metaFields";
import {
    converterAttachmentToFileWithPayload,
    createAttachmentType,
    emptyListRequest,
} from "utils/utils";
import { fileWithValidation, getExtraErrorsDescription, notEmptyMedia } from "utils/validators";

export type ProductBodyForm = {
    increment_id: number | null;
    slug: string;
    name: string;
    title: string;
    front_visible: boolean;
    reserved_area_visible: boolean;
    is_new: boolean;
    year: number | null;
    footer_template_id: string | undefined;
    collection_id: string | undefined;
    primary_category_id: string | undefined;
    secondary_category: string | undefined;
    thumbnail: DesktopMobileForm;
    cover: DesktopMobileForm;
    zip_2d?: AttachmentType;
    zip_3d?: AttachmentType;
    zip_dimension?: AttachmentType;
    block_group_id: string | null;
    text: MetaFields;
};

export type ProductFormHook = {
    setDesktopMobileImageThumbnail: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    setDesktopMobileImageCover: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    setSingleFile: (fileToSet: AttachmentType, path: string) => void;
    formHook: FormHookType<ProductBodyForm>;
    productModuleHook: LanguageOrderableImagesHook;
    allCollection: Array<OptionType>;
    templateOptions: OptionType[];
    allCategory: Array<OptionType>;
    productMeasureHook: LanguageOrderableImagesHook;
};

function useProductForm({ productId }: { productId?: string }): ProductFormHook {
    const { createProduct, updateProduct, getOneProduct } = useProductApi();
    const { getAllCollection } = useCollectionApi();
    const { getAllCategory } = useCategoryApi();
    const { getAllTemplate } = useTemplateApi();

    const { addSaveAction } = useSaveActionsContext();

    const schema: ObjectPropertyType = React.useMemo(() => {
        return {
            type: "object",
            properties: {
                increment_id: {
                    type: "simple",
                    validators: [],
                },
                slug: {
                    type: "simple",
                    validators: [notEmptyValidator],
                },
                name: {
                    type: "simple",
                    validators: [notEmptyValidator],
                },
                front_visible: {
                    type: "simple",
                    validators: [],
                },
                reserved_area_visible: {
                    type: "simple",
                    validators: [],
                },
                is_new: {
                    type: "simple",
                    validators: [],
                },
                footer_template_id: {
                    type: "simple",
                    validators: [],
                },
                year: {
                    type: "simple",
                    validators: [],
                },
                collection_id: {
                    type: "simple",
                    validators: [],
                },
                primary_category_id: {
                    type: "simple",
                    validators: [notEmptyValidator],
                },
                secondary_category: {
                    type: "simple",
                    validators: [notEmptyValidator],
                },
                block_group_id: {
                    type: "simple",
                    validators: [],
                },
                zip_2d: fileWithValidation,
                zip_3d: fileWithValidation,
                zip_dimension: fileWithValidation,
                thumbnail: notEmptyMedia,
                cover: notEmptyMedia,
                text: metaFieldsSchema,
            },
        };
    }, []);

    const initialValues: ProductBodyForm = React.useMemo(() => {
        return {
            increment_id: null,
            slug: "",
            name: "",
            title: "",
            front_visible: false,
            reserved_area_visible: false,
            is_new: false,
            year: null,
            product_id: "",
            footer_template_id: undefined,
            products: [],
            collection_id: undefined,
            primary_category_id: undefined,
            secondary_category: undefined,
            thumbnail: {
                id: undefined,
                desktop: createAttachmentType({ label: "Desktop" }),
                mobile: createAttachmentType({ label: "Mobile" }),
            },
            cover: {
                id: undefined,
                desktop: createAttachmentType({ label: "Desktop" }),
                mobile: createAttachmentType({ label: "Mobile" }),
            },
            zip_2d: createAttachmentType({ label: "File 2D" }),
            zip_3d: createAttachmentType({ label: "File 3D" }),
            zip_dimension: createAttachmentType({ label: "File Dimensione" }),
            block_group_id: null,
            text: emptyMetaFields(),
        };
    }, []);

    const formHook = useForm<ProductBodyForm>(schema, initialValues, getExtraErrorsDescription);

    const { showAllError, setForm, setValue, isValid, form } = formHook;

    const [productDetails, setProductDetails] = React.useState<ProductDetails | undefined>(
        undefined,
    );

    const [categories, setCategories] = React.useState<Array<CategoryListItem>>([]);

    const [allCategory, setAllCategory] = React.useState<Array<OptionType>>([]);

    React.useEffect(() => {
        const [apiCall] = getAllCategory(emptyListRequest);

        apiCall.then((response) => {
            setCategories(response.content);
        });
    }, [getAllCategory]);

    const { createProductMeasure, updateProductMeasure, deleteProductMeasure } =
        useProductMeasureApi();

    const { createProductModule, updateProductModule, deleteProductModule } = useProductModuleApi();

    const productMeasureHook = useLanguageOrderableImages({
        createImage: createProductMeasure,
        updateImage: updateProductMeasure,
        deleteImage: deleteProductMeasure,
        fieldId: "product_id",
    });

    const {
        saveLanguageOrderableImages: saveMeasureImages,
        initializeOrderableImage: initializeMeasureImage,
        formHook: productMeasureFormHook,
    } = productMeasureHook;

    const { isValid: productMeasureFormIsValid, showAllError: productMeasureShowAllError } =
        productMeasureFormHook;

    const productModuleHook = useLanguageOrderableImages({
        createImage: createProductModule,
        updateImage: updateProductModule,
        deleteImage: deleteProductModule,
        fieldId: "product_id",
    });

    const {
        saveLanguageOrderableImages: saveModuleImages,
        initializeOrderableImage: initializeModuleImage,
        formHook: productModuleFormHook,
    } = productModuleHook;

    const { isValid: productModuleFormIsValid, showAllError: productModuleShowAllError } =
        productModuleFormHook;

    React.useEffect(() => {
        if (productId) {
            const [apiCall, abortController] = getOneProduct({ id: productId });

            apiCall.then(setProductDetails);

            return () => abortController.abort();
        }
    }, [getOneProduct, productId]);

    React.useEffect(() => {
        if (productDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.collection_id = productDetails.collection_id ?? undefined;
                newForm.front_visible = productDetails.front_visible;
                newForm.reserved_area_visible = productDetails.reserved_area_visible;
                newForm.is_new = productDetails.is_new;
                newForm.increment_id = productDetails.increment_id;
                newForm.name = productDetails.name;
                newForm.title = productDetails.name;
                newForm.footer_template_id = productDetails.footer_template_id ?? undefined;
                newForm.slug = productDetails.slug;
                newForm.year = productDetails.year;
                newForm.block_group_id = productDetails.block_group_id;

                newForm.text.it = {
                    meta_title: productDetails.text?.it?.meta_title ?? "",
                    meta_description: productDetails.text?.it?.meta_description ?? "",
                    meta_keywords: productDetails.text?.it?.meta_keywords ?? "",
                };

                newForm.text.en = {
                    meta_title: productDetails.text?.en?.meta_title ?? "",
                    meta_description: productDetails.text?.en?.meta_description ?? "",
                    meta_keywords: productDetails.text?.en?.meta_keywords ?? "",
                };

                newForm.primary_category_id = productDetails.primary_category_id;

                const category = categories.find(
                    (item) => item.id === productDetails.primary_category_id,
                );

                if (category !== undefined) {
                    newForm.secondary_category = category.type;
                }

                if (productDetails.zip_2d) {
                    newForm.zip_2d = createAttachmentType({
                        id: productDetails.zip_2d.id ?? null,
                        label: "File 2D",
                        filename: productDetails.zip_2d.filename,
                        status: 2,
                    });
                }

                if (productDetails.zip_3d) {
                    newForm.zip_3d = createAttachmentType({
                        id: productDetails.zip_3d.id ?? null,
                        label: "File 3D",
                        filename: productDetails.zip_3d.filename,
                        status: 2,
                    });
                }

                if (productDetails.zip_dimension) {
                    newForm.zip_dimension = createAttachmentType({
                        id: productDetails.zip_dimension.id ?? null,
                        label: "File Dimensione",
                        filename: productDetails.zip_dimension.filename,
                        status: 2,
                    });
                }

                if (productDetails.thumbnail) {
                    newForm.thumbnail = {
                        id: productDetails.thumbnail.id,
                        desktop: createAttachmentType({
                            id: productDetails.thumbnail.desktop.id ?? null,
                            label: "Desktop",
                            filename: productDetails.thumbnail.desktop.filename,
                            status: 2,
                        }),
                        mobile: createAttachmentType({
                            id: productDetails.thumbnail.mobile.id ?? null,
                            label: "Mobile",
                            filename: productDetails.thumbnail.mobile.filename,
                            status: 2,
                        }),
                    };
                }

                if (productDetails.cover) {
                    newForm.cover = {
                        id: productDetails.cover.id,
                        desktop: createAttachmentType({
                            id: productDetails.cover.desktop.id ?? null,
                            label: "Desktop",
                            filename: productDetails.cover.desktop.filename,
                            status: 2,
                        }),
                        mobile: createAttachmentType({
                            id: productDetails.cover.mobile.id ?? null,
                            label: "Mobile",
                            filename: productDetails.cover.mobile.filename,
                            status: 2,
                        }),
                    };
                }

                return newForm;
            });
        }
    }, [setForm, productDetails, categories]);

    // complete orderable images form from data
    React.useEffect(() => {
        if (productDetails) {
            initializeMeasureImage(productDetails.measures);
            initializeModuleImage(productDetails.modules);
        }
    }, [initializeMeasureImage, initializeModuleImage, productDetails]);

    const { setDesktopMobileImage: setDesktopMobileImageThumbnail } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "thumbnail",
    });

    const { setDesktopMobileImage: setDesktopMobileImageCover } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "cover",
    });

    const { setSingleFile } = useSingleFile({
        setForm: setForm,
    });

    const [allCollection, setAllCollection] = React.useState<Array<OptionType>>([]);

    React.useEffect(() => {
        const [apiCall, abortController] = getAllCollection(emptyListRequest);

        apiCall.then((allCollectionResponse) => {
            setAllCollection(() => {
                const options: Array<OptionType> = [];

                allCollectionResponse.content.forEach((item) => {
                    return options.push({ value: item.id, label: item.name });
                });

                return options;
            });
        });

        return () => abortController.abort();
    }, [getAllCollection]);

    React.useEffect(() => {
        if (
            [IndoorOrOutdoor.indoor, IndoorOrOutdoor.outdoor].indexOf(
                form.secondary_category as IndoorOrOutdoor,
            ) !== -1
        ) {
            if (form.primary_category_id) {
                const actualCategory = categories.find(
                    (item) => item.id === form.primary_category_id,
                );

                if (actualCategory && actualCategory.type !== form.secondary_category) {
                    setValue("primary_category_id", null);
                }
            }

            setAllCategory(
                categories
                    .filter((item) => item.type === form.secondary_category)
                    .map((item) => ({ value: item.id, label: item.name })),
            );
        } else {
            setAllCategory([
                {
                    value: "",
                    label: "Compilare prima categoria 1",
                },
            ]);
        }
    }, [setValue, form.primary_category_id, form.secondary_category, categories]);

    const [templateOptions, setTemplateOptions] = React.useState<Array<OptionType>>([]);

    React.useEffect(() => {
        const [apiCall] = getAllTemplate(emptyListRequest);

        apiCall.then((allTemplateResponse) => {
            setTemplateOptions(() => {
                const options: Array<OptionType> = [];

                allTemplateResponse.content.forEach((item) => {
                    return options.push({ value: item.id, label: item.name });
                });

                return options;
            });
        });
    }, [getAllTemplate]);

    React.useEffect(() => {
        addSaveAction("useProductForm", () => {
            if (isValid && productMeasureFormIsValid && productModuleFormIsValid) {
                const promises: Promise<unknown>[] = [];

                const body: ProductBody = {
                    increment_id: form.increment_id ?? null,
                    slug: form.slug,
                    name: form.name,
                    footer_template_id: form.footer_template_id ?? null,
                    front_visible: form.front_visible,
                    reserved_area_visible: form.reserved_area_visible,
                    is_new: form.is_new,
                    year: form.year,
                    collection_id:
                        _.isString(form.collection_id) && form.collection_id.length > 0
                            ? form.collection_id
                            : null,
                    primary_category_id: form.primary_category_id ?? "",
                    thumbnail: {
                        id: form.thumbnail.id,
                        desktop: converterAttachmentToFileWithPayload(form.thumbnail.desktop),
                        mobile: converterAttachmentToFileWithPayload(form.thumbnail.mobile),
                    },
                    cover: {
                        id: form.cover.id,
                        desktop: converterAttachmentToFileWithPayload(form.cover.desktop),
                        mobile: converterAttachmentToFileWithPayload(form.cover.mobile),
                    },
                    zip_2d: converterAttachmentToFileWithPayload(form.zip_2d),
                    zip_3d: converterAttachmentToFileWithPayload(form.zip_3d),
                    zip_dimension: converterAttachmentToFileWithPayload(form.zip_dimension),
                    text: {
                        it: { ...form.text.it },
                        en: { ...form.text.en },
                    },
                };

                // Se ci sono i dettagli del prodotto
                if (productDetails) {
                    // Se non c'è il file dello zip_2d e c'è il file salvato sul prodotto allora vuol dire che stanno togliendo il file
                    if (form.zip_2d && form.zip_2d.status === 0 && productDetails.zip_2d_id) {
                        // quindi passo l'id a NULL per non avere più il riferimento al file
                        body.zip_2d_id = null;
                    }

                    // Se non c'è il file dello zip_3d e c'è il file salvato sul prodotto allora vuol dire che stanno togliendo il file
                    if (form.zip_3d && form.zip_3d.status === 0 && productDetails.zip_3d_id) {
                        // quindi passo l'id a NULL per non avere più il riferimento al file
                        body.zip_3d_id = null;
                    }

                    // Se non c'è il file dello zip_dimension e c'è il file salvato sul prodotto allora vuol dire che stanno togliendo il file
                    if (
                        form.zip_dimension &&
                        form.zip_dimension.status === 0 &&
                        productDetails.zip_dimension_id
                    ) {
                        // quindi passo l'id a NULL per non avere più il riferimento al file
                        body.zip_dimension_id = null;
                    }
                }

                if (productId) {
                    const [apiCall] = updateProduct({ id: productId }, body);

                    promises.push(apiCall);

                    promises.push(saveMeasureImages(productId));

                    promises.push(saveModuleImages(productId));
                } else {
                    promises.push(
                        new Promise((resolve, reject) => {
                            const [apiCall] = createProduct(body);

                            apiCall
                                .then((response) => {
                                    Promise.all([
                                        saveMeasureImages(response.id),
                                        saveModuleImages(response.id),
                                    ])
                                        .then(() => resolve(response))
                                        .catch(reject);
                                })
                                .catch(reject);
                        }),
                    );
                }

                return Promise.all(promises);
            } else {
                showAllError();
                productMeasureShowAllError();
                productModuleShowAllError();

                return Promise.reject("INVALID_FORM");
            }
        });
    }, [
        saveMeasureImages,
        saveModuleImages,
        createProduct,
        updateProduct,
        addSaveAction,
        showAllError,
        productMeasureShowAllError,
        productModuleShowAllError,
        productId,
        isValid,
        productMeasureFormIsValid,
        productModuleFormIsValid,
        form,
        productDetails,
    ]);

    return React.useMemo(
        (): ProductFormHook => ({
            setDesktopMobileImageThumbnail,
            setDesktopMobileImageCover,
            setSingleFile,
            productMeasureHook,
            productModuleHook,
            templateOptions,
            allCollection,
            allCategory,
            formHook,
        }),
        [
            setDesktopMobileImageThumbnail,
            setDesktopMobileImageCover,
            setSingleFile,
            productMeasureHook,
            productModuleHook,
            templateOptions,
            allCollection,
            allCategory,
            formHook,
        ],
    );
}

export default useProductForm;
